import { observer } from 'mobx-react-lite'
import { useEffect } from 'react'
import { useRepository } from 'hooks/auth'
import { useRootStore } from 'hooks/rootStore'
import { chatbotStore } from 'store/chatbotStore'
import {
  isSuppressedPath,
  resetIntercom,
  setIntercomLauncherHidden,
  startIntercom,
  stopIntercom,
} from 'config/intercom'

/**
 * Drives the Intercom Messenger, which hosts the Fin support agent. Renders
 * nothing. Mounted by ChatbotController only when chatbotProvider is 'fin'.
 *
 * Intercom has no portal-side URL targeting, so unlike HubSpotChatController
 * page targeting is entirely this component's job:
 * - boots the Messenger (except in E2E), above the authed/unauthed split so
 *   logged-out visitors get it too;
 * - hides the launcher on meeting routes;
 * - hides it during the pre-session quiz - a full-screen dialog no route check
 *   can catch, because the URL doesn't change;
 * - shuts the session down on logout so the next user on a shared machine
 *   doesn't inherit the conversation;
 * - tears the Messenger down on unmount, so flipping chatbotProvider at
 *   runtime doesn't leave two launchers on screen.
 */
export const IntercomController = observer(function IntercomController() {
  const rootStore = useRootStore()
  const repository = useRepository()

  useEffect(() => {
    startIntercom()
    return () => stopIntercom()
  }, [])

  useEffect(() => {
    const listener = () => resetIntercom()
    repository.onLogout(listener)
    return () => repository.offLogout(listener)
  }, [repository])

  // Both reads happen during render so the component re-runs when either the
  // route or an active suppressor changes.
  const hidden =
    chatbotStore.isSuppressed ||
    isSuppressedPath(rootStore.router.currentRoute?.path)

  useEffect(() => {
    setIntercomLauncherHidden(hidden)
  }, [hidden])

  return null
})
